// Where every card sits on the table.
//
// Pure geometry: given a GameState it returns a target transform per visible
// object, and the board scene springs the actual transforms toward those
// targets. Keeping it stateless means a card that changes zone does not need
// any special-case animation code -- its target simply moves, and the spring
// carries it there.
//
// The table lies in the XZ plane with the human at +Z, so "up the board" is -Z.

import { isUnitType } from '../rules/cards.ts';
import type { Rules } from '../rules/state.ts';
import type { TableLayout } from '../gfx/renderer.ts';
import { boardObjects, effectiveMight, zoneList } from '../rules/state.ts';
import type { GameObject, GameState, ObjectId, PlayerId } from '../rules/types.ts';

export type Vec3 = [number, number, number];

/** What a slot is for, so the view can style and filter by role. */
export type SlotRole =
  | 'hand' | 'unit' | 'gear' | 'rune' | 'battlefield' | 'legend' | 'champion'
  | 'deck' | 'trash' | 'chain';

export interface Slot {
  object: ObjectId;
  card: string;
  role: SlotRole;
  owner: PlayerId;
  position: Vec3;
  rotation: Vec3;
  scale: number;
  /** Card back toward the camera (opponent's hand, face-down decks). */
  faceDown: boolean;
  /** The human may click this. */
  interactive: boolean;
  exhausted: boolean;
  damage: number;
  /**
   * 143. What the unit's Might actually is right now -- auras, buffs and
   * this-turn modifiers included.
   *
   * This used to be the printed number, which is the one thing about a unit
   * that a player can already read off its face. A buffed unit looked exactly
   * like an unbuffed one.
   */
  might: number;
  /**
   * The printed number, so the view can say whether it has been changed.
   *
   * Null when the card has no Might at all. Gear and Runes do not have one,
   * and folding that to 0 made every Seal on the board wear a "0" it does not
   * print -- a stat where the card has none reads as a stat of zero.
   */
  printedMight: number | null;
  /** 137/414/441. Statuses worth a mark on the card. */
  statuses: readonly string[];
  /**
   * 152. Battlefields are printed landscape. Their artwork is 1040x744, so
   * drawing them on the portrait card quad squashes the image; the quad's world
   * extents are swapped instead, which leaves the UVs alone and maps a
   * landscape texture onto a landscape card.
   */
  landscape: boolean;
  /** Sort key so overlapping cards resolve predictably. */
  depth: number;
  /**
   * 108. What this slot is standing in for, when it is a pile.
   *
   * One card is drawn for a whole deck or trash, so the card alone cannot say
   * how deep the pile is. The count travels with the slot rather than being
   * looked up again by the view, so the number and the card it sits on cannot
   * come from two different readings of the state.
   */
  pile?: { zone: ListZoneName; count: number };
}

export interface LayoutOptions {
  human: PlayerId;
  /** Index of the battlefield currently in combat, for emphasis. */
  activeBattlefield?: number | null;
  /**
   * Where the players have arranged the cards standing in their bases.
   *
   * A cell in the owner's own base frame, not an offset from a slot: the base
   * spreads its cards to fit, so an offset would shift under every card that
   * enters or leaves and nobody's arrangement would survive their next play.
   * A cell is also what travels over the wire, for the same reason.
   */
  arrangement?: Arrangement;
  /**
   * The order the player has put their own hand in, by object id.
   *
   * Local to whoever is holding it and never sent anywhere: a hand is secret,
   * so there is no second view of it to keep in step -- and the order a player
   * likes their cards in is not something an opponent is entitled to know.
   * Ids the list does not mention keep their dealt order, at the end, so a card
   * just drawn appears where a drawn card should.
   */
  handOrder?: readonly ObjectId[];
}

/** Integer cells, by object id. Plain JSON so it can be sent as it stands. */
export type Arrangement = Record<number, [number, number]>;

// -- table metrics -----------------------------------------------------------

const FLAT: Vec3 = [-Math.PI / 2, 0, 0];
/**
 * Lying flat with the back up.
 *
 * A card is face down by being turned over, not by a flag: the mesh has a front
 * and a back face and the shader samples a different texture on each. The decks
 * were drawn with FLAT and showed their top card's face.
 *
 * *How* it is turned over matters. Flipping about the table's X axis
 * (`[+pi/2, 0, 0]`) does put the back up, but it also swings the card's top
 * edge round to the near side -- and the back face's UVs already mirror in X,
 * for a flip about the *vertical* axis. The two together left every deck on
 * the player's side showing its back rotated 180 degrees. Laying it flat and
 * rolling it over instead keeps the top edge pointing away from the player,
 * which is how a real deck sits.
 */
const FLAT_DOWN: Vec3 = [-Math.PI / 2, 0, Math.PI];

/**
 * 719. How far an attached card peeks out from under the card it is on.
 *
 * In table units before the host's own scale is applied, as [left, toward the
 * viewer]. A card is 0.72 long and about 0.52 across at unit scale, so this is
 * roughly a fifth of a card in each direction: enough that the attachment is
 * plainly a second card and can be clicked, little enough that it reads as
 * tucked under rather than laid beside.
 */
const ATTACH_OFFSET: [number, number] = [0.17, 0.13];

/** And drawn slightly smaller, which is what sells it as being underneath. */
const ATTACH_SCALE = 0.86;

/**
 * Rows run from the opponent's edge (-Z) to the player's (+Z). The whole table
 * has to fit one camera, so the depth budget is tight: every row added here
 * pushes the camera back and shrinks every card on screen.
 */
const ROW = {
  // Nearer than the player's own hand, deliberately. The vertical budget is
  // what limits how far the camera can zoom, and it is spent almost entirely
  // on the two hands -- but theirs is a row of card backs. There is nothing to
  // read there, so it gives up the room and the board gets it.
  opponentHand: -3.38,
  opponentRunes: -2.28,
  opponentBase: -1.54,
  opponentAtBattlefield: -0.62,
  battlefield: 0,
  playerAtBattlefield: 0.62,
  playerBase: 1.54,
  // 2.34, and the row is shallower to match (half-depth 0.28), which opens a
  // 0.12 gap above it while leaving its far edge at 2.62 -- clear of the mat's
  // edge fade, which begins at 2.62. The row used to butt straight against the
  // Base row at 1.94, so the labels set into the *bottom* rules above it --
  // CHAMPION, LEGEND, MAIN DECK -- were printed exactly where this row's top
  // brackets are, and neither could be read. Base sits 0.08 clear of the
  // battlefields for the same reason and has always read cleanly.
  playerRunes: 2.42,
  playerHand: 3.38,
};

/**
 * The camera rig, and the pitch derived from it.
 *
 * Both live here because the hand tilts are computed from the pitch: moving the
 * camera and leaving the tilts alone turns the hands edge-on and invisible.
 * Deriving the pitch from the rig rather than writing it twice means they
 * cannot drift apart.
 *
 * Looking down more steeply than 45 degrees shows the whole table at once,
 * which is what makes the rows read as rows rather than as a stack.
 */
export const CAMERA = {
  // A long lens from high up, not a wide lens from close in.
  //
  // The two rigs frame the same table, but a wide lens near the edge makes the
  // far half small and the near half huge -- the opponent's board reads as
  // distant scenery. Pulling the camera back to ~17 units and narrowing the
  // field to 20 degrees takes nearly all the foreshortening out, so both halves
  // are the same size and the whole table is legible at once. That, plus the
  // steep angle, is the near-top-down look card games are read from.
  height: 16.00,
  distance: 5.20,
  // Aimed past the middle, toward the player. What foreshortening is left
  // favours the far half -- and with the frustum this tight, what gets trimmed
  // has to be the top edge of the opponent's face-down hand rather than the
  // bottom of the player's own cards.
  targetZ: 0.30,
  /**
   * How far the whole view slides sideways, in world units.
   *
   * Negative aims the camera left of the table's centre, which puts the table
   * *right* of the screen's centre -- which is where it belongs, because the
   * left edge is where the card panel lives and the right edge is empty.
   * Applied to the eye and the target together, so it is a pan and not a skew:
   * moving only the target would swing the camera and shear the board.
   */
  panX: 0,
  // Wide enough to hold both hands.
  //
  // At 20 degrees the frustum was narrower than the table it was framing: the
  // rows fit, but the two hands sit a further 1.5 units past the outermost row
  // at each end, and both were cut in half by the top and bottom of the
  // window. A player could not see their own cards. 23.5 degrees is the
  // narrowest that holds the player's hand whole, which is the half that has
  // to be readable -- the opponent's is face down, and its top edge is still
  // trimmed.
  // Widened from 0.41 when the hands moved out to +/-3.38: at 0.41 the near
  // hand's lower edge projected 18px past the bottom of the canvas. The
  // frustum has to hold the hands, not just the mat -- and moving either the
  // hands or this without measuring the other cuts a player's own cards off.
  fov: 0.435,
  sway: 0.02,
};

/**
 * Derived, never written twice: the hand tilts and the turn plaque are all
 * square-on to this, and a camera moved without them turns them edge-on.
 */
export const CAMERA_PITCH = Math.atan2(CAMERA.height, CAMERA.distance);

/**
 * The player's hand faces the camera: a card tilted by the camera's own pitch
 * presents its front square-on. The extra bias leans it slightly more upright,
 * which reads better than dead-on.
 */
const HAND_TILT = -CAMERA_PITCH - 0.08;

/**
 * The opponent's hand shows its back instead, and is rolled by pi so the back's
 * wordmark stays upright. That roll negates the tilt's effect on the card
 * normal, so the tilt has to be measured past vertical rather than simply
 * mirrored -- mirroring it leaves the cards edge-on and invisible.
 */
const OPPONENT_HAND_TILT = -(Math.PI - CAMERA_PITCH);
const OPPONENT_HAND_ROLL = Math.PI;

/**
 * The table has two shapes, and which one is in use is a property of the
 * *device*, not of the window.
 *
 * A phone is a small screen held close, with a finger on it: the battlefields
 * have to be bigger to be legible, and the board is pushed right because the
 * card panel takes the left edge. A desktop has none of those problems, and
 * applying the phone's answers there made it worse -- the board sat off-centre
 * for no reason and the card art was hidden while there was room for it.
 *
 * Keyed on `(pointer: coarse)` rather than a width, because a desktop window
 * dragged narrow is still a desktop: it has a hover, a cursor and a big
 * screen, and it wants the desktop shape.
 */
export type ViewMode = 'desktop' | 'compact';

const RIGS = {
  desktop: {
    battlefieldScale: 1.04,
    // 0.82, not 1.04. The scale was set while the mat was small on screen;
    // once the camera moved in to fill the frame the same cards read as
    // oversized -- a base of four units looked like a base of four billboards.
    unitScale: 0.82,
    battlefieldSpacing: 2.24,
    battlefieldHalfW: 1.08,
    // Just clear of the mat's top edge (-2.72). Pushed further out, the gap
    // between their hand and the table reads as a hole in the middle of the
    // screen -- and it is space the mat could have been using.
    opponentHand: -3.14,
    // Further out than the opponent's, and deliberately past the bottom of the
    // frame. The player's own hand was held whole inside the frustum, and that
    // one rule was what stopped the camera moving in at all: everything else
    // fits, so the hand set the zoom for the whole table. A hand overhanging
    // the near edge is what a hand does anyway -- the top of each card, which
    // is the whole of what is worth reading, stays on screen.
    playerHand: 3.44,
    handScale: 1.16,
    // Their hand keeps the size it had. It is a row of card backs across the
    // top of the frame, and growing it with the player's own pushed its top
    // edge off the screen -- which is the one part of the far hand that says
    // it is there at all.
    opponentHandScale: 0.70,
    // Measured, not chosen: the one pair that keeps the opponent's hand on
    // screen while the player's overhangs the bottom edge by about a sixth of
    // a card. Wider than it was -- the size came from the cards, not the lens.
    // Chosen to make the *mat* fill the frame, which is what "bigger board"
    // meant all along -- not bigger battlefield cards. Measured: the mat goes
    // from 45% of the width and 67% of the height to 52% and 76%, with the
    // player's hand overhanging the bottom by a seventh of a card and two
    // thirds of the opponent's still showing.
    fov: 0.40,
    targetZ: 0.45,
    panX: 0,
    // Centred, and it stays centred. A desktop has room on both sides and
    // pushing the board off-centre there was asked for once and regretted.
    panFree: 0,
  },
  compact: {
    // Bigger battlefields, bought from the horizontal slack: the frustum
    // cannot zoom without cropping a hand, on any screen.
    battlefieldScale: 1.04,
    unitScale: 0.82,
    battlefieldSpacing: 2.24,
    battlefieldHalfW: 1.08,
    // Their hand is card backs; there is nothing to read, so it gives up the
    // room the zoom needs.
    opponentHand: -3.08,
    playerHand: 3.38,
    handScale: 1.10,
    opponentHandScale: 0.66,
    // Measured the same way as the desktop pair, by forcing this rig on a
    // desktop with `?view=compact`. The field of view is vertical, so the
    // vertical fit it is chosen for does not change with the width of the
    // screen -- what is measured here is what a phone gets.
    fov: 0.40,
    targetZ: 0.45,
    // Left is the card panel, right is empty.
    panX: -0.72,
    // ...and emptier still outside Discord, which floats its own controls over
    // that corner. In a browser or an installed app nothing is there but our
    // own buttons, so the table takes the room.
    panFree: -1.05,
  },
} as const;

let rig: (typeof RIGS)[ViewMode] = RIGS.desktop;

/**
 * Chooses the table's shape. Called once, before the mat is painted.
 *
 * The mat texture is drawn from these same rectangles, so this cannot change
 * mid-session without repainting it.
 */
export function setViewMode(mode: ViewMode, rightIsFree = false): void {
  rig = RIGS[mode];
  BATTLEFIELD_ZONE.halfW = rig.battlefieldHalfW;
  ROW.opponentHand = rig.opponentHand;
  ROW.playerHand = rig.playerHand;
  CAMERA.fov = rig.fov;
  CAMERA.targetZ = rig.targetZ;
  CAMERA.panX = rightIsFree ? rig.panFree : rig.panX;
}

/**
 * 152. A battlefield is printed landscape, so at the same scale it reads much
 * larger than the portrait cards standing at it -- its long edge is the card's
 * height. Units are scaled so their long edge matches the battlefield's, which
 * is what makes the board look like one set of cards rather than two.
 */
// Bigger than it was (0.80). The whole table is legible at a desk, but a
// Discord Activity is a small pane in a chat window, and the battlefields --
// the thing the game is actually about -- were the first thing to become
// unreadable there.
/**
 * Battlefields are drawn larger than the cards standing at them.
 *
 * They used to share one scale, so the board would read as a single set of
 * cards -- but the camera cannot zoom any further to make them bigger: with a
 * full hand on both sides the frustum's vertical budget is spent, and every
 * setting that enlarges the board cuts off somebody's hand. A battlefield is
 * printed landscape, so it grows into the *horizontal* slack, which a wide
 * screen has plenty of and which costs the hands nothing.
 */
const battlefieldScale = (): number => rig.battlefieldScale;
/** What stands *at* a battlefield, which still has to fit between the rows. */
export const unitScale = (): number => rig.unitScale;
/**
 * Runes are cards and are drawn as cards.
 *
 * At 0.50 they read as counters beside everything else on the table. This is
 * close to what a card in a base is (1.04), a little smaller only because a
 * dozen of them share one strip.
 */
const RUNE_SCALE = 0.72;
/** Legend, Champion and the piles all fill a one-card slot on the mat. */
const SLOT_SCALE = 0.74;
/**
 * How big a card in hand is drawn.
 *
 * Bigger than a card on the table: it is the thing being read and chosen from,
 * and the hand overlaps itself, so each card shows only a slice of its own
 * width until it is the one being pointed at.
 */
const handScale = (): number => rig.handScale;
/**
 * How much nearer the camera each card in a hand sits than the one to its
 * right, so a fan overlaps in one direction instead of fighting for depth.
 *
 * Small on purpose. The depth test is exact, so this only has to break a tie --
 * but the camera has perspective, so a card nearer the lens also projects lower
 * and larger. At 0.34 the hand visibly sloped 47 pixels down to the right,
 * which reads as a crooked hand rather than a stacked one.
 */
/**
 * The arch, and the stacking, and why each is on the axis it is on.
 *
 * The camera looks down from a long way up, and that decides which axis does
 * what. Depth from the lens is governed almost entirely by **y** -- about nine
 * times more strongly than by z -- while where a card lands on *screen* is
 * governed by **z**. Putting the arch in y therefore made the arch decide the
 * draw order: the middle card, being the highest, was the nearest, so the hand
 * stacked outward from the centre and read as a V. And the small z ramp added
 * to fix the order was doing its work on the one axis that shows, so it tilted
 * the whole row instead -- the right-hand card sitting visibly higher than the
 * left.
 *
 * So they swap. The arch is in z, symmetric about the middle; the order is a
 * gentle ramp in y, which is invisible on screen and decisive in depth.
 */
const HAND_ARCH = 0.36;
const HAND_ROLL = 0.52;
/**
 * The ramp has to out-pull the arch, and the margin is arithmetic.
 *
 * The camera faces (0, -0.955, -0.298) from here, so a step in y is worth
 * 0.955 of depth and a step in z is worth 0.298. The arch is `c * t*t`, whose
 * slope is steepest at the ends -- `2c * 0.5 = c` -- so keeping depth
 * monotonic needs `0.955 * m > 0.298 * c`, i.e. `m > 0.312 * c`. At c = 0.36
 * that is m > 0.112, and the first attempt used 0.10: just under, so the two
 * cancelled at the right-hand end and the last card came forward over its
 * neighbours. The measured depth steps gave it away by shrinking along the
 * hand -- 0.030, 0.024, 0.018, 0.012, 0.006, 0.0003 -- rather than by being
 * out of order, which is why "is it monotonic" was the wrong question to ask
 * of a settled board and a spring mid-flight answered it differently again.
 */
const HAND_STACK = 0.24;
/** How far out to the side the legend and champion sit. */
const PILE_SCALE = SLOT_SCALE;
type ListZoneName = 'mainDeck' | 'runeDeck' | 'trash';

/**
 * The printed playmat, zone by zone, in world units.
 *
 * Taken from the official mat rather than invented: Champion and Legend to the
 * left of a wide Base with the Main Deck at the right edge, and below them the
 * Rune Deck, a wide Runes strip and the Trash. Battlefields run across the
 * middle, shared.
 *
 * Every rectangle here is used twice -- once to place cards and once to draw
 * the mat -- so a zone and its label can never end up in different places. `z`
 * is the near-side (human) value; the opponent's half is the mirror.
 */
export interface Zone {
  readonly x: number;
  readonly z: number;
  readonly halfW: number;
  readonly halfD: number;
  readonly label: string;
}

export const MAT = {
  // Wider than it is deep, and deliberately so: the screen is, and the depth is
  // fixed by how many rows of cards have to stack up between the two hands.
  halfWidth: 3.60,
  // Deep enough that the outermost row -- which holds real cards, not tokens --
  // ends at 2.82 with the mat's edge fade beginning at 2.86. Squeezing the row
  // instead was what put the Rune Deck and Trash cards over their own labels.
  halfDepth: 2.96,
} as const;

/**
 * The battlefield zone. Deep enough to hold the battlefield card with a row of
 * units on each side of it, since that is where units at a battlefield stand.
 */
/**
 * Deep enough to hold a landscape battlefield and no deeper.
 *
 * The bases start at z 1.10 (row 1.54, half-depth 0.44), so anything past 1.06
 * here has the battlefield's printed rectangle running into the base's -- two
 * borders crossing and two labels sitting on top of each other. It was widened
 * to 1.16 while the battlefield cards were oversized and never put back.
 */
export const BATTLEFIELD_ZONE = { halfW: 1.08, halfD: 1.06 };

/** The near half's zones. The far half is this mirrored through z = 0. */
export const ZONES = {
  champion: { x: -3.05, z: ROW.playerBase, halfW: 0.30, halfD: 0.40, label: 'CHAMPION' },
  legend: { x: -2.38, z: ROW.playerBase, halfW: 0.30, halfD: 0.40, label: 'LEGEND' },
  // 0.40, matching Champion, Legend and Main Deck on the same row. At 0.44 the
  // Base rectangle reached z 1.98 while the Runes strip above it starts at
  // 1.94: the two boxes overlapped by 0.04 and the BASE label, which is set
  // into the bottom rule, landed inside the Runes zone.
  base: { x: 0.34, z: ROW.playerBase, halfW: 2.30, halfD: 0.40, label: 'BASE' },
  mainDeck: { x: 3.16, z: ROW.playerBase, halfW: 0.30, halfD: 0.40, label: 'MAIN DECK' },
  runeDeck: { x: -3.05, z: ROW.playerRunes, halfW: 0.30, halfD: 0.40, label: 'RUNE DECK' },
  runes: { x: 0.05, z: ROW.playerRunes, halfW: 2.62, halfD: 0.40, label: 'RUNES' },
  trash: { x: 3.16, z: ROW.playerRunes, halfW: 0.30, halfD: 0.40, label: 'TRASH' },
} as const satisfies Record<string, Zone>;

/**
 * The grid a player's base is arranged on.
 *
 * One cell is one card's footprint plus a hair: cards land beside each other
 * rather than overlapping, which is the point of snapping at all. Free
 * placement looked like a bug -- cards at slight angles to each other read as
 * drift -- and it could not be sent to the other player as anything an
 * onlooker would agree was the same board.
 *
 * The step matches the spread the base already uses, so an arranged card sits
 * where an unarranged one would have.
 */
export type ArrangeZone = 'base' | 'runes';

/**
 * The grid each arrangeable zone is arranged on.
 *
 * A grid must never reach outside the rectangle it belongs to. The base grid
 * once stepped 0.86 in z, which from the base row at 1.54 put row -1 at 0.68 --
 * and the battlefield row is at 0.62. A card "arranged" there stood exactly
 * where a conquering unit stands, so a move the engine had refused looked like
 * one it had made. The steps below keep a card's centre inside its own zone.
 */
export const ARRANGE_GRID = {
  // `rows: 0` -- a single line. Arranging is for putting cards *in order*, and
  // a second row turned a tidy row into a scatter: a card nudged half a step
  // forward sat out of line with its neighbours for no reason anyone could see.
  base: { x: 0.62, z: 0.30, cols: 3, rows: 0 },
  runes: { x: 0.42, z: 0.22, cols: 6, rows: 0 },
} as const satisfies Record<ArrangeZone, {
  x: number; z: number; cols: number; rows: number;
}>;

/** Kept for the server's bound check, which only needs the widest grid. */
export const ARRANGE_MAX = { cols: 6, rows: 1 } as const;

/** Where a cell sits in world space, for whichever half of the mat. */
export function zoneCell(
  cell: [number, number], where: ArrangeZone, mine: boolean,
): [number, number] {
  const zone = zoneFor(where, mine);
  const grid = ARRANGE_GRID[where];
  // The far half is the near half mirrored through the origin, so a cell has
  // to mirror with it -- otherwise the two players disagree about which way
  // "one to the left" runs, which is exactly what sitting opposite means.
  const sign = mine ? 1 : -1;
  return [zone.x + cell[0] * grid.x * sign, zone.z + cell[1] * grid.z * sign];
}

/** The nearest cell to a world point, clamped to the grid. */
export function cellAt(
  x: number, z: number, where: ArrangeZone, mine: boolean,
): [number, number] {
  const zone = zoneFor(where, mine);
  const grid = ARRANGE_GRID[where];
  const sign = mine ? 1 : -1;
  const gx = Math.round(((x - zone.x) * sign) / grid.x);
  const gz = Math.round(((z - zone.z) * sign) / grid.z);
  // `+ 0` normalises the negative zero `Math.round` returns just below centre.
  // It survives a round trip through JSON as plain 0, so a cell that kept it
  // would not equal the cell that came back from the server.
  return [
    Math.max(-grid.cols, Math.min(grid.cols, gx)) + 0,
    Math.max(-grid.rows, Math.min(grid.rows, gz)) + 0,
  ];
}

/** Whether a world point lies on the printed rectangle of a zone. */
export function insideZone(
  x: number, z: number, where: ArrangeZone, mine: boolean,
): boolean {
  const zone = zoneFor(where, mine);
  return Math.abs(x - zone.x) <= zone.halfW && Math.abs(z - zone.z) <= zone.halfD;
}

/** The same zone on whichever half of the mat a player sits. */
export function zoneFor(name: keyof typeof ZONES, mine: boolean): Zone {
  const z = ZONES[name];
  return mine ? z : { ...z, x: -z.x, z: -z.z };
}

/**
 * Cards in a row overlap once the row gets crowded rather than running off the
 * table. The steps below are sized for an exhausted card, which is turned on its
 * side and so is wider than a ready one -- spacing for the ready width makes a
 * row of tapped runes pile into an unreadable stack.
 */
function spread(count: number, index: number, step: number, maxWidth: number): number {
  if (count <= 1) return 0;
  const used = Math.min(step, maxWidth / (count - 1));
  return (index - (count - 1) / 2) * used;
}

/**
 * The playmat's zones, in the same world units the slots use.
 *
 * Exported from here rather than measured in the renderer so the mat and the
 * cards can never disagree about where a battlefield is.
 */
export function tableLayout(state: GameState, human: PlayerId): TableLayout {
  const count = state.battlefields.length;

  let playerHeld = 0;
  let opponentHeld = 0;
  state.battlefields.forEach((bf, i) => {
    if (bf.controller === human) playerHeld |= 1 << i;
    else if (bf.controller !== null) opponentHeld |= 1 << i;
  });

  return {
    // Fixed rather than derived from the battlefield count: the printed mat is
    // one size, and its side zones are what set its width.
    halfWidth: MAT.halfWidth,
    halfDepth: MAT.halfDepth,
    playerBaseZ: ROW.playerBase,
    opponentBaseZ: ROW.opponentBase,
    battlefieldZ: ROW.battlefield,
    battlefieldSpacing: rig.battlefieldSpacing,
    battlefieldCount: count,
    playerHeld,
    opponentHeld,
    accent: [0.42, 0.52, 0.78],
  };
}

export function layout(state: GameState, rules: Rules, opts: LayoutOptions): Slot[] {
  const slots: Slot[] = [];
  const { human } = opts;
  const opponent: PlayerId = human === 0 ? 1 : 0;

  const push = (
    o: GameObject, role: SlotRole, position: Vec3, rotation: Vec3,
    scale: number, faceDown: boolean, interactive: boolean, depth: number,
  ) => {
    const def = rules.index.get(o.card);
    slots.push({
      object: o.id, card: o.card, role, owner: o.controller,
      position, rotation, scale, faceDown, interactive,
      exhausted: o.exhausted, damage: o.damage,
      might: o.zone.kind === 'board' ? effectiveMight(state, rules, o) : (def.might ?? 0),
      printedMight: def.might,
      statuses: [...o.statuses],
      depth,
      landscape: def.type === 'battlefield',
    });
  };

  // -- battlefields ----------------------------------------------------------
  const bfX: number[] = [];
  state.battlefields.forEach((bf, i) => {
    const x = (i - (state.battlefields.length - 1) / 2) * rig.battlefieldSpacing;
    bfX.push(x);
    const card = state.objects[bf.object];
    if (!card) return;
    push(
      card, 'battlefield', [x, 0, ROW.battlefield], FLAT,
      battlefieldScale(), false, false, 0,
    );
  });

  // -- board objects ---------------------------------------------------------
  const groups = new Map<string, GameObject[]>();
  /** 719. Attachments, held back to be hung off their hosts afterwards. */
  const attached: GameObject[] = [];
  /** Where each top-most card was put, so its attachments can follow it. */
  const hosts = new Map<ObjectId, {
    at: Vec3; scale: number; rotation: Vec3; depth: number;
  }>();
  for (const o of boardObjects(state)) {
    const def = rules.index.get(o.card);
    if (def.type === 'battlefield') continue;
    const loc = o.location;
    if (!loc) continue;
    // 718.5.c. An attached card is not a card standing on its own: it rides on
    // its host, so it must not take a place in the row or push its neighbours
    // apart. Drawn as one of the row, an Equipment looked exactly like a
    // second permanent that happened to be next to a unit -- there was nothing
    // on the table to say the two were linked at all.
    if (o.attachedTo !== null) { attached.push(o); continue; }
    const key = `${loc.kind}:${loc.kind === 'base' ? loc.player : loc.index}:${o.controller}:${def.type === 'rune' ? 'r' : 'u'}`;
    const list = groups.get(key) ?? [];
    list.push(o);
    groups.set(key, list);
  }

  for (const [key, members] of groups) {
    const [kind, , controllerRaw, sort] = key.split(':');
    const controller = Number(controllerRaw) as PlayerId;
    const mine = controller === human;
    const isRune = sort === 'r';

    members.forEach((o, i) => {
      const def = rules.index.get(o.card);
      const loc = o.location!;

      let x: number;
      let z: number;
      let scale: number;

      if (isRune) {
        // Runes sit off to the side of their owner's base, out of the way --
        // and are arranged there, in the runes strip, never in the base. A
        // rune's *location* is its owner's base, so keying the arrangement off
        // the engine's zone rather than the drawn one would have flung every
        // rearranged rune into the middle of the board.
        scale = RUNE_SCALE;
        const strip = zoneFor('runes', mine);
        const cell = opts.arrangement?.[o.id];
        if (cell) {
          [x, z] = zoneCell(cell, 'runes', mine);
        } else {
          x = strip.x + spread(members.length, i, 0.42, strip.halfW * 1.85);
          z = strip.z;
        }
      } else if (kind === 'base') {
        scale = unitScale();
        const zone = zoneFor('base', mine);
        // A card the player has put somewhere stays there. Everything else
        // spreads to fit, as it always did.
        const cell = opts.arrangement?.[o.id];
        if (cell) {
          [x, z] = zoneCell(cell, 'base', mine);
        } else {
          x = zone.x + spread(members.length, i, 0.62, zone.halfW * 1.8);
          z = zone.z;
        }
      } else {
        scale = unitScale();
        const index = loc.kind === 'battlefield' ? loc.index : 0;
        x = (bfX[index] ?? 0) + spread(members.length, i, 0.54, 1.35);
        z = mine ? ROW.playerAtBattlefield : ROW.opponentAtBattlefield;
      }

      // 414.1.a. An exhausted card is turned ninety degrees.
      //
      // This has to go in the Y slot, not Z. Rotations compose as Rz*Ry*Rx, so
      // the flat tilt is applied first and leaves the card's normal pointing
      // along world +Y; only a rotation about that same axis keeps it lying on
      // the table. Turning it about Z instead stands the card on its edge,
      // where it reads as a vertical sliver rather than a rotated card.
      // Negative so the top edge swings to the player's right, the way you
      // physically tap a card.
      const rotation: Vec3 = [FLAT[0], o.exhausted ? -Math.PI / 2 : 0, 0];
      const role: SlotRole = isRune ? 'rune' : isUnitType(def.type) ? 'unit' : 'gear';

      // Units are clicked to move, runes to tap for resources -- and gear to
      // use the ability printed on it. Leaving gear out meant `pick` never
      // returned an Equipment, so 818's "Equip: attach this to a unit you
      // control" could not be reached by any gesture at all.
      const interactive = mine;
      const at: Vec3 = [x, 0.001 + i * 0.0015, z];
      hosts.set(o.id, { at, scale, rotation, depth: i });
      push(o, role, at, rotation, scale, false, interactive, i);
    });
  }

  // 719.3. Each attachment, tucked under the card it is attached to.
  //
  // Offset down and to the left and drawn a little smaller, the way a card
  // slid beneath another one reads: enough of it shows to be recognised and
  // clicked, and the host stays the card you look at. Because the position is
  // derived from the host's, it follows the host everywhere for free -- which
  // is the point, since an Equipment and the unit carrying it are at the same
  // location by rule and must never appear to be in two places.
  for (const o of attached) {
    const host = o.attachedTo !== null ? hosts.get(o.attachedTo) : undefined;
    if (!host) continue;
    const def = rules.index.get(o.card);
    const role: SlotRole = isUnitType(def.type) ? 'unit' : 'gear';
    const at: Vec3 = [
      host.at[0] - ATTACH_OFFSET[0] * host.scale,
      // Under the host: a lower y is further from the camera on this rig, so
      // the host draws over it rather than the other way round.
      host.at[1] - 0.0008,
      host.at[2] + ATTACH_OFFSET[1] * host.scale,
    ];
    // Interactive because it can still be chosen and still prints abilities
    // of its own (718.5.b) -- and because a player has to be able to read it.
    push(o, role, at, host.rotation, host.scale * ATTACH_SCALE, false,
      o.controller === human, host.depth);
  }

  // -- hands -----------------------------------------------------------------
  // Both hands are laid out by the same function so they cannot fan in
  // different directions or spread by different amounts. The step is smaller
  // than a card is wide, so the cards overlap the way a held hand does rather
  // than standing apart in a row.
  /**
   * The hand in the order its owner put it, with anything unmentioned -- a card
   * just drawn -- kept in dealt order at the end.
   *
   * Cards that have left the hand are ignored rather than removed, so the
   * stored order can outlive them: a card played and later returned to hand
   * goes back where its owner had it.
   */
  const orderHand = (hand: ObjectId[], order?: readonly ObjectId[]): ObjectId[] => {
    if (!order || order.length === 0) return hand;
    const rank = new Map(order.map((id, i) => [id, i]));
    const known = hand.filter((id) => rank.has(id)).sort((a, b) => rank.get(a)! - rank.get(b)!);
    return [...known, ...hand.filter((id) => !rank.has(id))];
  };

  const fan = (count: number, i: number, step: number, maxSpan: number) => {
    const t = count <= 1 ? 0 : i / (count - 1) - 0.5;
    return { t, x: t * Math.min(maxSpan, count * step) };
  };

  const humanHand = orderHand(zoneList(state, 'hand', human), opts.handOrder);
  humanHand.forEach((id, i) => {
    const o = state.objects[id];
    if (!o) return;
    const { t, x } = fan(humanHand.length, i, 0.44 * (rig.handScale / 0.95), 4.2);
    // A shallow arc, lifted toward the camera so the faces read cleanly.
    //
    // Held low: the hand is in front of the mat's near edge, and lifting it any
    // higher swings it up the screen and over the Runes strip behind it.
    // A held hand curves. The arc has to be big enough to *see* from a camera
    // looking almost straight down -- at 0.24 the bow measured a single pixel
    // on screen, which is a flat row of cards wearing a curve in the numbers.
    // Left card highest, and so nearest the lens, and so drawn over the next.
    const lift = 0.30 - t * HAND_STACK;
    push(
      o, 'hand',
      // Overlap runs one way: the leftmost card is nearest the camera and each
      // one to its right sits a little further back, so the hand stacks like a
      // held fan. `slot.depth` cannot do this -- nothing reads it, and the
      // order on screen is decided by the depth buffer -- so it has to be in
      // the position. Pushing *both* ends forward (`abs(t)`) made the middle
      // card the one underneath and the two ends the ones on top, which reads
      // as a V rather than a fan.
      // Ends nearer the viewer than the middle, which puts them lower on
      // screen: an arch, seen from here.
      [x, lift, ROW.playerHand + t * t * HAND_ARCH],
      // The fan is carried by the *roll*. A near-top-down camera flattens a
      // lift almost completely -- the arc measured four pixels on screen at
      // more than twice this curvature -- so the rotation is what makes a hand
      // read as held rather than as a row laid out on the table.
      // Rolled less than it was. A card turned 27 degrees shows its face
      // foreshortened by cos(27) -- about a ninth narrower -- so the cards at
      // the ends of the fan read as skinnier than the ones in the middle. The
      // fan still reads as a fan at half that.
      [HAND_TILT, 0, -t * HAND_ROLL],
      handScale(), false, true, i,
    );
  });

  const aiHand = zoneList(state, 'hand', opponent);
  aiHand.forEach((id, i) => {
    const o = state.objects[id];
    if (!o) return;
    const { t, x } = fan(aiHand.length, i, 0.34, 2.5);
    // The arc mirrors as well as the roll. A fan is held opening away from the
    // person holding it, so the one opposite you reads upside down: their
    // middle card sits *lower* on screen than their ends, where yours sits
    // higher. Curving both the same way made their hand look like a second
    // hand of yours, floating at the far edge of the table.
    // No stacking ramp on this side, and that is the point: the ramp is what
    // orders the player's own hand left-over-right, and it costs a few pixels
    // of tilt to do it. Across the table the cards are identical backs, so the
    // order buys nothing and the tilt is the only thing that shows -- it made
    // their hand lean 15px, which reads as a crooked fan rather than an arch.
    // Without it the arch is exactly symmetric and the middle card simply sits
    // over its neighbours on both sides.
    const lift = 0.30;
    // The opponent's hand is rolled by PI so its back reads upright, and that
    // roll reverses the direction a further roll appears to turn -- so the fan
    // needs the opposite sign to the player's, or their hand curves the same
    // way on screen as yours and reads as a hand held by nobody. The sign flip
    // was described in this comment for a long time without being in the code.
    //
    // The stack mirrors with it: their leftmost card is the viewer's rightmost.
    push(
      // Mirrored with the rest of their half: a fan opens away from whoever
      // holds it, so theirs bows the other way from here.
      o, 'hand', [x, lift, ROW.opponentHand - t * t * HAND_ARCH],
      [OPPONENT_HAND_TILT, 0, OPPONENT_HAND_ROLL + t * HAND_ROLL],
      rig.opponentHandScale, true, false, i,
    );
  });

  // -- legends and champions -------------------------------------------------
  for (const player of [human, opponent] as PlayerId[]) {
    const mine = player === human;
    // Legend at the base row, champion one step toward the middle. Both stay
    // well clear of the bottom-right corner, where the HUD's controls live.
    const legendAt = zoneFor('legend', mine);
    const champAt = zoneFor('champion', mine);

    const legend = zoneList(state, 'legendZone', player)[0];
    if (legend !== undefined && state.objects[legend]) {
      push(
        // A legend is clickable for the same reason gear is: several print an
        // activated ability ("exhaust me to ready up to 2 runes").
        state.objects[legend]!, 'legend', [legendAt.x, 0, legendAt.z],
        FLAT, SLOT_SCALE, false, mine, 0,
      );
    }

    const champ = zoneList(state, 'championZone', player)[0];
    if (champ !== undefined && state.objects[champ]) {
      push(
        state.objects[champ]!, 'champion', [champAt.x, 0, champAt.z],
        FLAT, SLOT_SCALE, false, mine, 0,
      );
    }
  }

  // -- the chain -------------------------------------------------------------
  state.chain.forEach((item, i) => {
    const o = state.objects[item.source];
    if (!o || o.zone.kind !== 'chain') return;
    push(
      o, 'chain', [-0.1 + i * 0.16, 0.85 + i * 0.05, ROW.battlefield + 0.45],
      [-0.9, 0, 0.05], 0.66, false, false, i,
    );
  });

  // -- the piles ------------------------------------------------------------
  // 108. A real table shows its decks. They are drawn face down at the outer
  // edge of each base, one card standing for the pile, so a glance tells you
  // whether someone is close to decking out.
  for (const player of [human, opponent] as PlayerId[]) {
    const mine = player === human;
    // Each pile sits in its own printed slot on the mat.
    const piles: ListZoneName[] = ['mainDeck', 'runeDeck', 'trash'];
    for (const zone of piles) {
      const at = zoneFor(zone, mine);
      const { x, z } = at;
      const ids = zoneList(state, zone, player);
      const top = ids[ids.length - 1];
      if (top === undefined) continue;
      const card = state.objects[top];
      if (!card) continue;
      const down = zone !== 'trash';
      push(
        card, zone === 'trash' ? 'trash' : 'deck', [x, 0, z],
        down ? FLAT_DOWN : FLAT,
        PILE_SCALE, down, false, 0,
      );
      slots[slots.length - 1]!.pile = { zone, count: ids.length };
    }
  }

  return slots;
}

/** Screen-space position of a battlefield, for drop targets and HUD anchors. */
export function battlefieldAnchor(state: GameState, index: number): Vec3 {
  const x = (index - (state.battlefields.length - 1) / 2) * rig.battlefieldSpacing;
  return [x, 0, ROW.battlefield];
}

export function baseAnchor(human: PlayerId, player: PlayerId): Vec3 {
  return [0, 0, player === human ? ROW.playerBase : ROW.opponentBase];
}
